import {
  useDeleteShippingLocationMutation,
  useShippingLocationActiveCountsQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import Ghost from '@/gf/components/Ghost'
import useMsgs from '@/gf/hooks/useMsgs'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { ApolloError } from '@apollo/client'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface Props {
  locationId: string
  locationName: string
  open: boolean
  onClose: () => void
}

const DeleteLocationModal = ({ locationId, locationName, open, onClose }: Props) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const navigate = useNavigate()
  const [deleteLocation] = useDeleteShippingLocationMutation({ client })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: countsData, loading: countsLoading } = useShippingLocationActiveCountsQuery({
    client,
    variables: { id: locationId },
    skip: !open,
  })

  const activeRfqs = countsData?.shippingLocationActiveCounts?.activeRfqs ?? 0
  const activeOrders = countsData?.shippingLocationActiveCounts?.activeOrders ?? 0
  const hasActiveItems = activeRfqs > 0 || activeOrders > 0

  const handleDelete = async () => {
    setIsSubmitting(true)

    try {
      await deleteLocation({
        variables: { id: locationId },
      })

      msgr.add('Location deleted successfully.', 'positive')
      navigate('/locations')
    } catch (err) {
      const error = err as ApolloError
      const message = error.graphQLErrors.reduce((acc, e) => `${acc}${e.message}`, '')
      msgr.add(message || 'Failed to delete location', 'negative')
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Delete Location</h3>
          <CloseModalButton onClick={onClose} />
        </div>

        <div className="mb-6 space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{locationName}</strong>?
          </p>

          {countsLoading ? (
            <Ghost className="w-full h-16 block" />
          ) : hasActiveItems ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Warning: This location has active items</p>
                  <ul className="mt-1 list-disc list-inside">
                    {activeRfqs > 0 && (
                      <li>
                        {activeRfqs} active request{activeRfqs !== 1 ? 's' : ''}
                      </li>
                    )}
                    {activeOrders > 0 && (
                      <li>
                        {activeOrders} active order{activeOrders !== 1 ? 's' : ''}
                      </li>
                    )}
                  </ul>
                  <p className="mt-2">
                    Deleting this location will not cancel these items, but they will remain
                    associated with the deleted location.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <p className="text-sm text-gray-500">You can reactivate this location later if needed.</p>
        </div>

        <div className="flex justify-end gap-3">
          <Action.S onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Action.S>
          <Action.P
            className="bg-red-600 hover:bg-red-700 border-red-700 hover:border-red-700"
            onClick={handleDelete}
            performing={isSubmitting}
            disabled={isSubmitting || countsLoading}
          >
            Delete
          </Action.P>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteLocationModal
